import { useState } from 'react';
import { MARKETING_FEE, MIN_MARGIN, TARGET_MARGIN } from '../utils/prebid';
import { PREBID_CSS, money, pct } from '../components/PrebidResult';

/* Pre-Bid (Roofing) — every roofing cost typed by hand.
   Squares + sale price, then labor, material, commission and other costs — all flat
   dollar totals for the job (not per square). The 2.7% marketing fee is added from the
   sale price, same as the other Pre-Bid calculators. Verdict: at least 40% margin,
   plus the price for 55%. "Price for" figures keep these same costs: cost ÷ (1 − margin). */

const num = v => { const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, '')); return Number.isFinite(n) && n > 0 ? n : 0; };
const MIN = Math.round(MIN_MARGIN * 100);
const GOAL = Math.round(TARGET_MARGIN * 100);
const ROOF_TYPES = ['', 'Overlay', 'Tear-Off', 'Tear Sheet'];

export default function RoofingPrebid() {
  const [v, setV] = useState({ type: '', squares: '', sale: '', labor: '', materials: '', commission: '', other: '' });
  const set = (k, x) => setV(s => ({ ...s, [k]: x }));

  const sq = num(v.squares);
  const sale = num(v.sale);
  const labor = num(v.labor);
  const materials = num(v.materials);
  const commission = num(v.commission);
  const other = num(v.other);
  const marketing = sale * MARKETING_FEE;
  const totalCost = labor + materials + commission + other + marketing;
  const profit = sale - totalCost;
  const margin = sale ? profit / sale : 0;
  const ready = sq > 0 && sale > 0 && (labor + materials) > 0;
  const ok = margin >= MIN_MARGIN;
  const minSale = totalCost / (1 - MIN_MARGIN);
  const targetSale = totalCost / (1 - TARGET_MARGIN);
  const per = x => (sq ? `${money(x / sq)} / sq` : '');

  const rows = [
    ['Labor', labor, `entered · ${per(labor)}`],
    ['Material', materials, `entered · ${per(materials)}`],
    ['Commission', commission, `entered · ${per(commission)}`],
    ['Other costs', other, `entered · ${per(other)}`],
  ].filter(r => r[1]);

  return (
    <>
      <style>{PREBID_CSS}</style>
      <div className="page-header">
        <h2>Pre-Bid Calculator (Roofing)</h2>
        <div style={{ fontSize: 13, color: '#888' }}>
          Type the squares, sale price and every roofing cost yourself as flat dollar amounts (not per square). To save it with a customer, use the Pre-Bid tab in Add / Update Record and pick "Roofing (type costs)". The {(MARKETING_FEE * 100).toFixed(1)}% marketing fee is added automatically. Every job needs at least a {MIN}% margin.
        </div>
      </div>
      <div className="page-body">
        <div className="pb-grid">
          <div className="pb-field">
            <label>Roof type (optional)</label>
            <select className="form-input" value={v.type} onChange={e => set('type', e.target.value)}>
              {ROOF_TYPES.map(t => <option key={t} value={t}>{t || '—'}</option>)}
            </select>
          </div>
          <div className="pb-field">
            <label>Squares</label>
            <input className="form-input" inputMode="decimal" placeholder="e.g. 25" value={v.squares} onChange={e => set('squares', e.target.value)} />
          </div>
          <div className="pb-field">
            <label>Sale price (total job)</label>
            <input className="form-input" inputMode="decimal" placeholder="e.g. 30,000" value={v.sale} onChange={e => set('sale', e.target.value)} />
          </div>
        </div>
        <div className="pb-grid">
          <div className="pb-field">
            <label>Labor — flat price ($)</label>
            <input className="form-input" inputMode="decimal" placeholder="0" value={v.labor} onChange={e => set('labor', e.target.value)} />
          </div>
          <div className="pb-field">
            <label>Material — flat price ($)</label>
            <input className="form-input" inputMode="decimal" placeholder="0" value={v.materials} onChange={e => set('materials', e.target.value)} />
          </div>
          <div className="pb-field">
            <label>Commission total ($)</label>
            <input className="form-input" inputMode="decimal" placeholder="0" value={v.commission} onChange={e => set('commission', e.target.value)} />
          </div>
          <div className="pb-field">
            <label>Other costs ($, optional)</label>
            <input className="form-input" inputMode="decimal" placeholder="0" value={v.other} onChange={e => set('other', e.target.value)} />
          </div>
        </div>

        {!ready ? (
          <div className="pb-card" style={{ color: '#7a8a82' }}>Enter the squares, sale price and labor or material to check the margin.</div>
        ) : (
          <>
            <div className={'pb-verdict ' + (ok ? 'ok' : 'bad')}>
              <div className="big">{ok ? '✓ Good to go' : '⚠ Red flag'}: {pct(margin)} margin</div>
              <div className="sub">
                {v.type ? `Roofing — ${v.type}, ${sq} sq: ` : `Roofing, ${sq} sq: `}
                {ok ? `This bid clears the ${MIN}% minimum.` : `Below the ${MIN}% minimum. The price needs to be at least ${money(minSale)}, that's ${money(minSale - sale)} more.`}
              </div>
              <div className="pb-tiles">
                <div className="pb-tile"><div className="k">Sale price</div><div className="v">{money(sale)}</div><div className="s">{per(sale)}</div></div>
                <div className="pb-tile"><div className="k">Total cost</div><div className="v">{money(totalCost)}</div><div className="s">{per(totalCost)}</div></div>
                <div className="pb-tile"><div className="k">Profit</div><div className="v">{money(profit)}</div><div className="s">{per(profit)}</div></div>
                <div className="pb-tile"><div className="k">Price for {MIN}%</div><div className="v">{money(minSale)}</div><div className="s">{per(minSale)}</div></div>
                <div className="pb-tile goal"><div className="k">Price for {GOAL}%</div><div className="v">{money(targetSale)}</div>
                  <div className="s">{per(targetSale)} · {margin >= TARGET_MARGIN ? `already at ${GOAL}%+` : `${money(targetSale - sale)} more`}</div></div>
              </div>
            </div>

            <div className="pb-card">
              {rows.map(([label, amt, hint]) => (
                <div className="pb-row" key={label}><span>{label}<span className="hint">{hint}</span></span><b>{money(amt)}</b></div>
              ))}
              <div className="pb-row">
                <span>Marketing fee<span className="hint">{(MARKETING_FEE * 100).toFixed(1)}% of {money(sale)}</span></span>
                <b>{money(marketing)}</b>
              </div>
              <div className="pb-row pb-total"><span>Total cost</span><b>{money(totalCost)}</b></div>
              <div className="pb-row">
                <span>Profit<span className="hint">{money(sale)} − {money(totalCost)}</span></span>
                <b>{money(profit)}</b>
              </div>
              <div className="pb-row">
                <span>Margin<span className="hint">profit ÷ sale price · minimum {MIN}%</span></span>
                <b style={{ color: ok ? '#1B5E20' : '#B71C1C' }}>{pct(margin)}</b>
              </div>
            </div>
            <div className="pb-note">Price for {MIN}% and {GOAL}% keep these same costs: total cost ÷ (1 − margin).</div>
          </>
        )}
      </div>
    </>
  );
}
